/**
 * User + Organization — minimal wire shapes shared across features.
 *
 * Mirrors ``ando-frontend/app/types/organizations.ts`` (v1) but keeps only
 * the columns every feature touches (login, topbar, settings header).
 * Per-feature extensions (e.g. ``ConnectorLicense``) live in their own
 * slice and import from here.
 */

import { z } from "zod";
import { IsoTimestampSchema, UuidSchema } from "./common.js";

export const OrgRoleSchema = z.enum(["owner", "admin", "member"]);
export type OrgRole = z.infer<typeof OrgRoleSchema>;

export const DeploymentModeSchema = z.enum(["shared", "dedicated", "isolated"]);
export type DeploymentMode = z.infer<typeof DeploymentModeSchema>;

export const UserSchema = z.object({
  id: UuidSchema,
  email: z.email(),
  display_name: z.string().optional(),
  org_id: UuidSchema.optional(),
  roles: z.array(z.string()).optional(),
  role: OrgRoleSchema.optional(),
  created_at: IsoTimestampSchema.optional(),
  updated_at: IsoTimestampSchema.optional(),
});
export type User = z.infer<typeof UserSchema>;

export const OrgSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  slug: z.string().optional(),
  is_active: z.boolean().optional(),
  usage_multiplier: z.number().optional(),
  max_members: z.number().int().optional(),
  billing_email: z.email().nullable().optional(),
  deployment_mode: DeploymentModeSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  created_at: IsoTimestampSchema.optional(),
  updated_at: IsoTimestampSchema.optional(),
});
export type Org = z.infer<typeof OrgSchema>;
